import { useState } from "react";
import { PackageSearch } from "lucide-react";
import { api, formatApiError, money } from "@/lib/api";
import { Reveal, Eyebrow, MaskedLine } from "@/components/Reveal";

const field = "w-full bg-transparent border border-white/15 px-5 py-4 text-sm placeholder:text-cream/30 focus:outline-none focus:border-gold/60 transition-colors";

export default function TrackOrder() {
  const [form, setForm] = useState({ order_id: "", email: "" });
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setOrder(null);
    try {
      const { data } = await api.post("/orders/lookup", form);
      setOrder(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pt-16" data-testid="track-order-page">
      <div className="max-w-xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-24 sm:pb-32">
        <Reveal><Eyebrow className="text-center">No Account Needed</Eyebrow></Reveal>
        <h1 className="font-display font-extrabold uppercase tracking-tight text-4xl sm:text-6xl mt-4 text-center leading-[0.9]">
          <MaskedLine delay={0.1}><span className="text-cream">Track</span></MaskedLine>
          <MaskedLine delay={0.2}><span className="text-outline">Order</span></MaskedLine>
        </h1>
        <Reveal delay={0.25}>
          <p className="text-center text-cream/50 text-sm mt-6">
            Your order number (JN-XXXXXXXX) and the email you checked out with. That's all it takes.
          </p>
        </Reveal>

        <Reveal delay={0.3}>
          <form onSubmit={submit} className="mt-10 space-y-4" data-testid="track-order-form">
            <input
              required
              value={form.order_id}
              onChange={(e) => setForm({ ...form, order_id: e.target.value.toUpperCase() })}
              placeholder="Order number — JN-XXXXXXXX"
              className={`${field} font-mono2 tracking-[0.15em]`}
              data-testid="track-order-id-input"
            />
            <input
              required
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="Checkout email"
              className={field}
              data-testid="track-order-email-input"
            />
            {error && <p className="text-red-400 text-xs font-mono2 tracking-wide" data-testid="track-order-error">{error}</p>}
            <button type="submit" disabled={busy} className="w-full bg-gold text-noir-bg font-mono2 text-[11px] tracking-[0.25em] uppercase py-4 hover:bg-cream transition-colors duration-300 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="track-order-submit">
              <PackageSearch size={15} /> {busy ? "Looking…" : "Find My Order"}
            </button>
          </form>
        </Reveal>

        {order && (
          <Reveal className="mt-10">
            <div className="border border-gold/30 p-8" data-testid="track-order-result">
              <div className="flex justify-between items-center mb-6">
                <span className="font-mono2 text-sm text-gold" data-testid="track-result-id">{order.order_id}</span>
                <span className={`font-mono2 text-[9px] tracking-[0.2em] uppercase px-2 py-1 border ${order.status === "shipped" ? "bg-cream text-noir-bg border-cream" : "bg-gold/10 border-gold/40 text-gold"}`} data-testid="track-result-status">
                  {order.status === "shipped" ? "Shipped" : "Paid — Preparing"}
                </span>
              </div>
              <ul className="space-y-2 mb-6">
                {order.items.map((i, idx) => (
                  <li key={idx} className="flex justify-between text-sm text-cream/60">
                    <span>{i.qty} × {i.name} — Size {i.size}</span>
                    <span className="font-mono2 text-gold">{money(i.price * i.qty)}</span>
                  </li>
                ))}
              </ul>
              {order.tracking_number ? (
                <div className="border-t border-white/10 pt-5" data-testid="track-result-tracking">
                  <p className="font-mono2 text-[9px] tracking-[0.3em] uppercase text-cream/40 mb-2">Tracking</p>
                  <p className="text-sm">{order.carrier} — <span className="font-mono2 text-gold">{order.tracking_number}</span></p>
                </div>
              ) : (
                <p className="border-t border-white/10 pt-5 text-xs text-cream/40">
                  Being prepared in numbered packaging. You'll get a shipping email the moment it moves.
                </p>
              )}
              <div className="flex justify-between text-xs font-mono2 text-cream/40 mt-5 pt-5 border-t border-white/10">
                <span>{new Date(order.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}</span>
                <span className="text-gold">{money(order.amount)}</span>
              </div>
            </div>
          </Reveal>
        )}
      </div>
    </div>
  );
}
